import React, {
   useState,
   useEffect,
   useRef,
   memo,
   useCallback,
   useMemo,
   useContext,
} from "react";
import { getCurrentCDSToken } from "./Auth";
import { useUser } from "./User";
import { AudioContext, IntegratedAudioPlayer } from "./AudioPlayer";
import { useTheme } from "./ThemeContext"; // Import the theme hook
import config from "./config";

// Get styles based on theme for progress component
function getProgressStyles(theme) {
   const isLight = theme === "light";
   return {
      container: {
         width: "100%",
         display: "flex",
         flexDirection: "column",
         alignItems: "center",
      },
      title: {
         color: "var(--text-primary)",
         fontSize: "1.125rem",
         fontWeight: "medium",
         marginBottom: "1.5rem",
         textAlign: "center",
         fontFamily: "Inter, sans-serif",
      },
      timeRemaining: {
         color: "var(--text-primary)",
         marginBottom: "0.75rem",
         textAlign: "center",
      },
      progressBar: {
         width: "100%",
         backgroundColor: isLight
            ? "rgba(0, 0, 0, 0.1)"
            : "rgba(255, 255, 255, 0.1)",
         height: "0.5rem",
         borderRadius: "9999px",
         overflow: "hidden",
         marginBottom: "2rem",
      },
      progressFill: {
         height: "100%",
         background:
            "linear-gradient(90deg, var(--accent-secondary), var(--accent-primary))",
         borderRadius: "9999px",
         transition: "width 0.5s ease-in-out",
      },
      statusText: {
         color: isLight ? "var(--text-muted)" : "var(--text-dimmed)",
         fontSize: "0.875rem",
         fontStyle: "italic",
         textAlign: "center",
      },
   };
}

// Audiobook Generation Progress Component
const AudiobookGenerationProgress = ({
   startTime = new Date(),
   estimatedTotalMinutes = 15,
   isComplete = false,
   taskProgress = 0,
   statusMessage = "", // We'll keep the parameter but not display it
}) => {
   const { theme } = useTheme();
   const styles = getProgressStyles(theme);
   const [progress, setProgress] = useState(taskProgress || 0);
   const [timeRemaining, setTimeRemaining] = useState(estimatedTotalMinutes);

   // Update progress and time remaining every second
   useEffect(() => {
      if (isComplete) {
         setProgress(100);
         setTimeRemaining(0);
         return;
      }

      const timer = setInterval(() => {
         // Calculate elapsed time in minutes
         const elapsedMs = new Date() - startTime;
         const elapsedMinutes = elapsedMs / (1000 * 60);

         // Calculate progress percentage based on elapsed time
         const calculatedProgress = Math.min(
            Math.round((elapsedMinutes / estimatedTotalMinutes) * 100),
            99
         );

         // Use the higher of server-reported progress or time-based progress
         const newProgress = Math.max(calculatedProgress, taskProgress);

         // Calculate remaining time
         const remainingMinutes = Math.max(
            Math.round(estimatedTotalMinutes - elapsedMinutes),
            1
         );

         setProgress(newProgress);
         setTimeRemaining(remainingMinutes);
      }, 1000);

      return () => clearInterval(timer);
   }, [startTime, estimatedTotalMinutes, isComplete, taskProgress]);

   return (
      <div style={styles.container}>
         <div style={styles.title}>
            Great stories take time. Your audiobook is being created...
         </div>

         <div style={styles.timeRemaining}>{timeRemaining} min remaining</div>

         <div style={styles.progressBar}>
            <div
               style={{
                  ...styles.progressFill,
                  width: `${progress}%`,
               }}
            ></div>
         </div>

         {/* Status message removed */}
      </div>
   );
};

// Get styles for PromptInput component
const getPromptInputStyles = (theme) => {
   const isLight = theme === "light";
   return {
      container: {
         width: "70%",
         maxWidth: "800px",
      },
      charCounter: {
         color: isLight ? "var(--text-muted)" : "#aaa",
      },
      charCounterError: {
         color: "#ef4444",
      },
      errorText: {
         color: "#ef4444",
      },
      textarea: {
         backgroundColor: "var(--bg-secondary)",
         color: "var(--text-primary)",
         border: `1px solid ${isLight ? "var(--border-color)" : "#444"}`,
         borderRadius: "0.375rem",
         padding: "0.75rem",
         marginBottom: "0.5rem",
         width: "100%",
         resize: "vertical",
      },
      textareaError: {
         backgroundColor: "var(--bg-secondary)",
         color: "var(--text-primary)",
         border: "1px solid #ef4444",
         borderRadius: "0.375rem",
         padding: "0.75rem",
         marginBottom: "0.5rem",
         width: "100%",
         resize: "vertical",
      },
      buttonContainer: {
         display: "flex",
         justifyContent: "space-between",
         alignItems: "center",
         marginBottom: "0.5rem",
      },
      button: {
         marginTop: "2rem",
         padding: "0.25rem 2rem",
         borderRadius: "0.5rem",
         fontWeight: "bold",
         transition: "all 0.2s ease",
      },
      adminOptionsContainer: {
         backgroundColor: "var(--bg-secondary)",
         border: `1px solid ${isLight ? "var(--border-color)" : "#444"}`,
         borderRadius: "0.375rem",
         padding: "1rem",
         marginTop: "1rem",
         marginBottom: "1rem",
      },
      adminOptionsTitle: {
         fontWeight: "bold",
         color: "var(--text-primary)",
         marginBottom: "0.75rem",
         fontFamily: "Inter, sans-serif",
      },
      checkboxContainer: {
         display: "flex",
         alignItems: "center",
         marginBottom: "0.75rem",
      },
      checkbox: {
         marginRight: "0.5rem",
      },
      checkboxLabel: {
         color: "var(--text-primary)",
         fontSize: "0.9rem",
      },
      authorInputContainer: {
         marginBottom: "0.5rem",
      },
      authorInput: {
         backgroundColor: "var(--bg-secondary)",
         color: "var(--text-primary)",
         border: `1px solid ${isLight ? "var(--border-color)" : "#444"}`,
         borderRadius: "0.375rem",
         padding: "0.5rem",
         width: "100%",
      },
      adminBadge: {
         display: "inline-block",
         backgroundColor: isLight ? "#f87171" : "#ef4444",
         color: "white",
         fontWeight: "bold",
         fontSize: "0.7rem",
         padding: "0.15rem 0.4rem",
         borderRadius: "0.25rem",
         marginLeft: "0.5rem",
         textTransform: "uppercase",
      },
   };
};

// Prompt Input Component - Handles user prompt entry with admin options
const PromptInput = memo(({ onSubmit, isLoading }) => {
   const { theme } = useTheme();
   const styles = getPromptInputStyles(theme);
   const [prompt, setPrompt] = useState("");
   const [error, setError] = useState("");
   const MAX_CHARS = 2000;

   // Admin settings
   const { isAdmin } = useUser();
   const [generateAllChapters, setGenerateAllChapters] = useState(false);
   const [publishImmediately, setPublishImmediately] = useState(false);
   const [defaultAuthor, setDefaultAuthor] = useState("");

   const handleChange = (e) => {
      const inputValue = e.target.value;
      setPrompt(inputValue);

      if (inputValue.length > MAX_CHARS) {
         setError(
            `Your prompt exceeds the ${MAX_CHARS} character limit. Please shorten it.`
         );
      } else {
         setError("");
      }
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      if (prompt.trim() && prompt.length <= MAX_CHARS) {
         // If user is admin, include admin options
         if (isAdmin) {
            onSubmit(prompt, {
               generateAllChapters,
               publishImmediately,
               defaultAuthor: defaultAuthor.trim(),
            });
         } else {
            onSubmit(prompt);
         }
      }
   };

   // Calculate remaining characters
   const remainingChars = MAX_CHARS - prompt.length;
   const isOverLimit = remainingChars < 0;

   return (
      <form onSubmit={handleSubmit} className="d-flex justify-content-center">
         <div className="d-flex flex-column" style={styles.container}>
            {error && (
               <div className="d-flex justify-content-between align-items-center mb-2">
                  <div
                     style={
                        isOverLimit
                           ? styles.charCounterError
                           : styles.charCounter
                     }
                  >
                     {prompt.length}/{MAX_CHARS} characters
                  </div>
                  <div style={styles.errorText}>{error}</div>
               </div>
            )}
            <textarea
               style={isOverLimit ? styles.textareaError : styles.textarea}
               placeholder="Enter your audiobook prompt here"
               value={prompt}
               onChange={handleChange}
               rows="4"
               disabled={isLoading}
            />

            {/* Admin Options Section */}
            {isAdmin && (
               <div style={styles.adminOptionsContainer}>
                  <div style={styles.adminOptionsTitle}>
                     Admin Generation Options{" "}
                     <span style={styles.adminBadge}>Admin</span>
                  </div>

                  <div style={styles.checkboxContainer}>
                     <input
                        type="checkbox"
                        id="generateAllChapters"
                        checked={generateAllChapters}
                        onChange={(e) =>
                           setGenerateAllChapters(e.target.checked)
                        }
                        style={styles.checkbox}
                        disabled={isLoading}
                     />
                     <label
                        htmlFor="generateAllChapters"
                        style={styles.checkboxLabel}
                     >
                        Generate all chapters immediately
                     </label>
                  </div>

                  <div style={styles.checkboxContainer}>
                     <input
                        type="checkbox"
                        id="publishImmediately"
                        checked={publishImmediately}
                        onChange={(e) =>
                           setPublishImmediately(e.target.checked)
                        }
                        style={styles.checkbox}
                        disabled={isLoading}
                     />
                     <label
                        htmlFor="publishImmediately"
                        style={styles.checkboxLabel}
                     >
                        Publish book immediately after generation
                     </label>
                  </div>

                  <div style={styles.authorInputContainer}>
                     <label
                        htmlFor="defaultAuthor"
                        style={styles.checkboxLabel}
                     >
                        Author Name:
                     </label>
                     <input
                        type="text"
                        id="defaultAuthor"
                        value={defaultAuthor}
                        onChange={(e) => setDefaultAuthor(e.target.value)}
                        placeholder="Enter author name"
                        style={styles.authorInput}
                        disabled={isLoading}
                     />
                  </div>
               </div>
            )}

            <div style={styles.buttonContainer}>
               <div></div>
               <button
                  type="submit"
                  style={{
                     ...styles.button,
                     backgroundColor:
                        isLoading || !prompt.trim() || isOverLimit
                           ? "transparent"
                           : "var(--accent-secondary)",
                     color:
                        isLoading || !prompt.trim() || isOverLimit
                           ? theme === "light"
                              ? "var(--text-muted)"
                              : "#888"
                           : "white",
                     border:
                        isLoading || !prompt.trim() || isOverLimit
                           ? `2px solid ${
                                theme === "light"
                                   ? "var(--border-color)"
                                   : "#444"
                             }`
                           : "none",
                  }}
                  disabled={isLoading || !prompt.trim() || isOverLimit}
               >
                  {isLoading ? "Generating..." : "Generate"}
               </button>
            </div>
         </div>
      </form>
   );
});

// Get styles for QueueStatus component
function getQueueStatusStyles(theme) {
   const isLight = theme === "light";
   return {
      container: {
         marginTop: "0.75rem",
      },
      connectingContainer: {
         marginTop: "0.75rem",
         display: "flex",
         alignItems: "center",
      },
      connectingText: {
         color: "var(--text-primary)",
         marginLeft: "0.5rem",
      },
      alertInfo: {
         backgroundColor: isLight
            ? "rgba(66, 133, 244, 0.1)"
            : "rgba(66, 133, 244, 0.1)",
         color: isLight ? "var(--accent-secondary)" : "#63b3ed",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         border: `1px solid ${
            isLight ? "rgba(66, 133, 244, 0.2)" : "rgba(66, 133, 244, 0.3)"
         }`,
      },
      alertPrimary: {
         backgroundColor: isLight
            ? "rgba(79, 70, 229, 0.1)"
            : "rgba(79, 70, 229, 0.1)",
         color: isLight ? "#4f46e5" : "#818cf8",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         border: `1px solid ${
            isLight ? "rgba(79, 70, 229, 0.2)" : "rgba(79, 70, 229, 0.3)"
         }`,
      },
      alertSuccess: {
         backgroundColor: isLight
            ? "rgba(16, 185, 129, 0.1)"
            : "rgba(16, 185, 129, 0.1)",
         color: isLight ? "var(--accent-primary)" : "#10b981",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         border: `1px solid ${
            isLight ? "rgba(16, 185, 129, 0.2)" : "rgba(16, 185, 129, 0.3)"
         }`,
      },
      alertDanger: {
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.1)"
            : "rgba(239, 68, 68, 0.1)",
         color: "#ef4444",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         border: `1px solid ${
            isLight ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.3)"
         }`,
      },
      alertWarning: {
         backgroundColor: isLight
            ? "rgba(245, 158, 11, 0.1)"
            : "rgba(245, 158, 11, 0.1)",
         color: isLight ? "#f59e0b" : "#fbbf24",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         border: `1px solid ${
            isLight ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.3)"
         }`,
      },
      flexContainer: {
         display: "flex",
         alignItems: "center",
      },
      messageContainer: {
         marginLeft: "0.5rem",
      },
      title: {
         fontWeight: "bold",
      },
      message: {
         fontSize: "0.875rem",
         marginTop: "0.25rem",
      },
   };
}

/**
 * Queue Status Component - Displays generation progress via WebSocket
 * Modified to only show progress bar and remaining time, without status messages
 */
const QueueStatus = memo(
   ({
      taskUuid,
      onComplete,
      onError,
      isReadyForWs,
      wsUrlBase,
      candidateUuids,
   }) => {
      const { theme } = useTheme();
      const styles = getQueueStatusStyles(theme);
      const [status, setStatus] = useState(null);
      const [startTime, setStartTime] = useState(new Date());
      const [estimatedTotalMinutes, setEstimatedTotalMinutes] = useState(15); // Default estimate
      const [taskProgress, setTaskProgress] = useState(0);
      const [statusMessage, setStatusMessage] = useState(""); // Keep for internal use but don't display
      const wsRef = useRef(null);
      const completedRef = useRef(false);

      useEffect(() => {
         let isSubscribed = true;
         console.log(
            "QueueStatus effect running, taskUuid:",
            taskUuid,
            "isReadyForWs:",
            isReadyForWs
         );

         const connectWebSocket = () => {
            if (!isReadyForWs) {
               console.log("Not ready for WebSocket yet");
               return;
            }

            // If we've already completed, don't reconnect
            if (completedRef.current) {
               console.log(
                  "Task already completed, not reconnecting WebSocket"
               );
               return;
            }

            const token = localStorage.getItem("token");
            if (!token) {
               if (isSubscribed) onError("No host token found.");
               return;
            }

            // Close existing connection if any
            if (wsRef.current) {
               console.log("Closing existing WebSocket connection");
               wsRef.current.close();
               wsRef.current = null;
            }

            const wsUrl = `${wsUrlBase}?token=${encodeURIComponent(
               token
            )}&task_uuid=${encodeURIComponent(taskUuid)}`;
            console.log("Connecting to WebSocket:", wsUrl);

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
               console.log("WebSocket connected successfully");
            };

            ws.onmessage = (e) => {
               console.log("Raw WS message:", e.data);
               if (!isSubscribed) return;

               try {
                  const update = JSON.parse(e.data);
                  console.log("WS Update parsed:", update);

                  setStatus(update);

                  // Extract progress information
                  if (update.progress_percentage) {
                     setTaskProgress(update.progress_percentage);
                  }

                  // Extract estimated time information
                  if (update.estimated_total_minutes) {
                     setEstimatedTotalMinutes(update.estimated_total_minutes);
                  }

                  // Extract status message if available (for internal use only)
                  if (update.message && update.message !== "updated") {
                     setStatusMessage(update.message);
                  }

                  // Handle completion
                  if (update.status === "done") {
                     console.log(
                        "Received 'done' status, marking as completed"
                     );
                     completedRef.current = true;

                     if (isSubscribed) {
                        console.log(
                           "Calling onComplete callback with candidateUuids:",
                           candidateUuids
                        );
                        onComplete(candidateUuids);
                     }

                     ws.close();
                  }
               } catch (err) {
                  console.error("Error parsing WS message:", err);
               }
            };

            ws.onerror = (err) => {
               console.error("WebSocket error:", err);
               if (isSubscribed) {
                  onError("WebSocket connection error");
               }
            };

            ws.onclose = (event) => {
               console.log(
                  `WebSocket closed: Code=${event.code}, Reason=${
                     event.reason || "No reason provided"
                  }`
               );
            };
         };

         if (taskUuid && isReadyForWs) {
            connectWebSocket();
         }

         return () => {
            console.log("QueueStatus cleanup running");
            isSubscribed = false;

            if (wsRef.current) {
               console.log("Closing WebSocket during cleanup");
               wsRef.current.close();
               wsRef.current = null;
            }
         };
      }, [
         taskUuid,
         onComplete,
         onError,
         isReadyForWs,
         wsUrlBase,
         candidateUuids,
      ]);

      // Show a connecting indicator if we don't have status yet
      if (!status) {
         return (
            <div style={styles.connectingContainer}>
               <div
                  className="spinner-border text me-2 inter-font"
                  role="status"
               >
                  <span className="visually-hidden">Loading...</span>
               </div>
               <span style={styles.connectingText}>
                  Connecting to generation service...
               </span>
            </div>
         );
      }

      // Render progress bar when in_proses or waiting_in_queue status
      if (
         status.status === "in_proses" ||
         status.status === "waiting_in_queue"
      ) {
         return (
            <div style={styles.container}>
               {/* Simplified status indicator - just a spinner */}
               <div
                  style={
                     status.status === "waiting_in_queue"
                        ? styles.alertInfo
                        : styles.alertPrimary
                  }
               >
                  <div style={styles.flexContainer}>
                     <div className="spinner-border spinner-border-sm me-2" />
                     <div style={styles.messageContainer}>
                        <strong style={styles.title}>
                           {status.status === "waiting_in_queue"
                              ? "In Queue"
                              : "Generating"}
                        </strong>
                     </div>
                  </div>
               </div>

               {status.status === "in_proses" && (
                  <AudiobookGenerationProgress
                     startTime={startTime}
                     estimatedTotalMinutes={estimatedTotalMinutes}
                     isComplete={false}
                     taskProgress={taskProgress}
                     // Status message param kept but not used in the component
                     statusMessage={statusMessage}
                  />
               )}
            </div>
         );
      }

      // Handle completed states
      if (status.status === "done") {
         return (
            <div style={styles.container}>
               <div style={styles.alertSuccess}>
                  <div style={styles.flexContainer}>
                     <i className="bi bi-check-circle me-2"></i>
                     <div style={styles.messageContainer}>
                        <strong style={styles.title}>Generation Complete</strong>
                     </div>
                  </div>
               </div>
            </div>
         );
      }

      // Handle error states
      if (status.status === "failed" || status.status === "rejected") {
         return (
            <div style={styles.container}>
               <div style={status.status === "failed" ? styles.alertDanger : styles.alertWarning}>
                  <div style={styles.flexContainer}>
                     <i className="bi bi-exclamation-triangle me-2"></i>
                     <div style={styles.messageContainer}>
                        <strong style={styles.title}>
                           {status.status === "failed" ? "Generation Failed" : "Request Rejected"}
                        </strong>
                     </div>
                  </div>
               </div>
            </div>
         );
      }

      // Default state (connecting, unknown, etc.)
      return (
         <div style={styles.container}>
            <div style={styles.alertInfo}>
               <div style={styles.flexContainer}>
                  <div className="spinner-border spinner-border-sm me-2" />
                  <div style={styles.messageContainer}>
                     <strong style={styles.title}>Processing</strong>
                  </div>
               </div>
            </div>
         </div>
      );
   }
);

// Get styles for BookCard component
const getBookCardStyles = (theme) => {
   const isLight = theme === "light";
   return {
      wrapper: {
         width: "20rem",
         margin: "1rem",
         height: "40rem",
         boxSizing: "border-box",
         backgroundColor: "transparent",
         borderRadius: "1.5rem",
         overflow: "hidden",
      },
      card: {
         cursor: "pointer",
         height: "100%",
         transition: "all 0.2s ease",
         display: "flex",
         flexDirection: "column",
         boxSizing: "border-box",
         borderRadius: "1.5rem",
         overflow: "hidden",
         position: "relative",
         backgroundColor: isLight
            ? "rgba(255, 255, 255, 0.8)"
            : "rgba(255, 255, 255, 0.1)",
      },
      cardNormal: {
         border: `3px solid ${
            isLight ? "rgba(230, 235, 240, 0.6)" : "rgba(222, 226, 230, 0.2)"
         }`,
         boxShadow: isLight
            ? "0 4px 12px rgba(0,0,0,0.05)"
            : "0 4px 12px rgba(0,0,0,0.1)",
      },
      cardHovered: {
         border: `3px solid ${
            isLight ? "rgba(118, 181, 247, 0.4)" : "rgba(118, 181, 247, 0.3)"
         }`,
         boxShadow: isLight
            ? "0 4px 12px rgba(0,0,0,0.05)"
            : "0 4px 12px rgba(0,0,0,0.1)",
      },

      cardSelected: {
         border: `3px solid var(--accent-secondary)`,
         boxShadow: isLight
            ? "0 4px 12px rgba(0,123,255,0.15)"
            : "0 4px 12px rgba(0,123,255,0.2)",
      },
      header: {
         borderTopLeftRadius: "8px",
         borderTopRightRadius: "8px",
         backgroundColor: "transparent",
         padding: "10px 15px",
         borderBottom: "None",
         flexShrink: 0,
      },
      title: {
         overflow: "hidden",
         textOverflow: "ellipsis",
         whiteSpace: "nowrap",
         color: "var(--text-primary)",
         fontWeight: "700",
         fontSize: "0.8rem",
         textAlign: "center",
         marginTop: "0.5rem",
         fontFamily: "Inter, sans-serif",
      },
      imageContainer: {
         width: "100%",
         height: "20rem",
         borderRadius: "1.5rem",
         position: "relative",
         backgroundColor: "transparent",
         overflow: "hidden",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         flexShrink: 0,
         padding: "1px",
      },
      loadingSpinner: {
         color: "var(--accent-secondary)",
      },
      errorText: {
         color: "var(--text-primary)",
      },
      image: {
         maxWidth: "100%",
         maxHeight: "100%",
         objectFit: "contain",
         opacity: 1,
         transition: "opacity 0.3s ease-in-out",
      },
      tagsContainer: {
         margin: "0.25rem 0.5rem",
         overflow: "hidden",
         maxHeight: "4.5rem",
         display: "flex",
         flexWrap: "wrap",
         justifyContent: "center",
         alignContent: "flex-start",
      },
      tag: {
         display: "inline-block",
         padding: "0.15rem 0.35rem",
         margin: "0.15rem 0.15rem",
         borderRadius: "0.4rem",
         fontSize: "0.5rem",
         backgroundColor: isLight ? "#EFEFEF" : "#444",
         color: isLight ? "#000" : "#fff",
         fontWeight: "700",
         border: isLight ? "1px solid #e5e7eb" : "1px solid #555",
         boxShadow: isLight
            ? "0px 2px 3px 0px rgba(158, 158, 158, 0.25)"
            : "0px 2px 3px 0px rgba(0, 0, 0, 0.25)",
         whiteSpace: "nowrap",
         maxWidth: "100%",
         overflow: "hidden",
         textOverflow: "ellipsis",
         fontFamily: "Inter, sans-serif",
      },
      contentContainer: {
         padding: "6px 12px",
         flex: "1",
         position: "relative",
         display: "flex",
         flexDirection: "column",
         minHeight: 0,
      },
      scrollIndicator: {
         position: "absolute",
         top: "-22px",
         left: "50%",
         transform: "translateX(-50%)",
         backgroundColor: isLight
            ? "rgba(0, 123, 255, 0.7)"
            : "rgba(0, 123, 255, 0.8)",
         color: "white",
         borderRadius: "4px",
         padding: "2px 8px",
         fontSize: "0.6rem",
         zIndex: 5,
         animation: "fadeInOut 2s infinite",
         boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
         whiteSpace: "nowrap",
      },
      textWrapper: {
         height: "100%",
         overflow: "auto",
         scrollbarWidth: "thin",
         position: "relative",
         borderRadius: "0 0 8px 8px",
         padding: "4px",
         transition: "all 0.3s ease",
         width: "100%",
         boxSizing: "border-box",
      },
      text: {
         fontSize: "0.8rem",
         color: isLight ? "var(--text-primary)" : "#E1E1E1",
         marginBottom: "20px",
         paddingRight: "5px",
         paddingBottom: "8px",
      },
      scrollGradient: {
         position: "sticky",
         bottom: "0",
         left: "0",
         right: "0",
         height: "20px",
         pointerEvents: "none",
         display: "flex",
         justifyContent: "center",
         alignItems: "center",
         zIndex: 5,
         transition: "opacity 0.2s ease",
      },
      scrollIcon: {
         color: "var(--accent-secondary)",
         animation: "pulse 1.5s infinite",
         fontSize: "12px",
      },
      selectedBadge: {
         position: "absolute",
         top: "-8px",
         right: "-8px",
         backgroundColor: "var(--accent-secondary)",
         color: "white",
         borderRadius: "50%",
         width: "24px",
         height: "24px",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
         zIndex: 10,
         transition: "opacity 0.2s ease",
      },
   };
};

// BookCard Component - Updated to fix background bleed and hover border issues
const BookCard = memo(({ book, isSelected, onSelect, theme }) => {
   const styles = getBookCardStyles(theme);
   const { userId } = useUser();
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(false);
   const [imageSrc, setImageSrc] = useState(null);
   const [isHovered, setIsHovered] = useState(false);

   // Use a fixed size instead of dynamic sizing
   const IMAGE_SIZE = "512x512";

   useEffect(() => {
      let isMounted = true;

      const loadCoverImage = async () => {
         try {
            // For non-public books, ensure we have a CDS token
            let cdsToken = null;
            if (!book.isPublic) {
               cdsToken = await getCurrentCDSToken();
            }

            // Include user_id in the URL for the CDS
            const imageUrl = `${config.cdsUrl}/user/audiobooks/${book.uuid}/cover?size=${IMAGE_SIZE}&user_id=${userId}`;

            const headers = {
               Accept: "image/jpeg,image/png,*/*",
               Origin: config.primaryUrl,
            };

            if (cdsToken) {
               headers["Authorization"] = JSON.stringify(cdsToken);
            }

            const response = await fetch(imageUrl, {
               method: "GET",
               headers,
               credentials: "include",
               mode: "cors",
            });

            if (!response.ok) {
               throw new Error(`Failed to load image: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            if (isMounted) {
               setImageSrc(objectUrl);
               setLoading(false);
               setError(false);
            }
         } catch (err) {
            console.error("Error loading image:", err);
            if (isMounted) {
               setError(true);
               setLoading(false);
            }
         }
      };

      loadCoverImage();

      return () => {
         isMounted = false;
         if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
         }
      };
   }, [book.uuid, book.isPublic, userId]);

   return (
      <div className="book-card" style={styles.wrapper}>
         <div
            className="card"
            style={{
               ...styles.card,
               ...(isSelected
                  ? styles.cardSelected
                  : isHovered
                  ? styles.cardHovered
                  : styles.cardNormal),
            }}
            onClick={onSelect}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
         >
            <div className="card-header" style={styles.header}>
               <h5 className="card-title mb-0 inter-font" style={styles.title}>
                  {book.title}
               </h5>
            </div>

            {/* Integrated cover image section */}
            <div style={styles.imageContainer}>
               {loading && (
                  <div
                     className="spinner-border"
                     role="status"
                     style={styles.loadingSpinner}
                  >
                     <span className="visually-hidden">Loading...</span>
                  </div>
               )}
               {error && (
                  <span style={styles.errorText}>Cover Not Available</span>
               )}
               {imageSrc && (
                  <img
                     src={imageSrc}
                     alt={`Cover for ${book.title}`}
                     style={styles.image}
                  />
               )}
            </div>

            {/*Tags section */}
            {book.tags && book.tags.length > 0 && (
               <div style={styles.tagsContainer}>
                  {book.tags.map((tag, index) => (
                     <span
                        key={index}
                        className="inter-font"
                        style={styles.tag}
                     >
                        {tag}
                     </span>
                  ))}
               </div>
            )}

            {/* Text content section with scrolling indicators */}
            <div
               style={{
                  ...styles.contentContainer,
                  borderTop:
                     "1px dashed rgba(" +
                     (theme === "light" ? "128, 128, 128," : "204, 204, 204,") +
                     (isSelected ? "1" : "0") +
                     ")",
               }}
            >
               {book.highlight && (
                  <>
                     {/* Visual scrolling indicator that appears when selected*/}
                     {isSelected && (
                        <div style={styles.scrollIndicator}>
                           <i className="bi bi-mouse"></i> Scroll for more
                        </div>
                     )}

                     {/* Create a wrapper div that maintains consistent dimensions */}
                     <div
                        style={{
                           ...styles.textWrapper,
                           overflowY: isSelected ? "scroll" : "hidden",
                           scrollbarColor: isSelected
                              ? theme === "light"
                                 ? "var(--accent-secondary) #f0f0f0"
                                 : "#007bff #333"
                              : "transparent transparent",
                           background: isSelected
                              ? theme === "light"
                                 ? "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 85%, rgba(0,123,255,0.05) 100%)"
                                 : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 85%, rgba(0,123,255,0.1) 100%)"
                              : "none",
                        }}
                        className={isSelected ? "force-scrollbar" : ""}
                     >
                        <p style={styles.text}>{book.highlight}</p>

                        {/* Scroll gradient indicator at bottom when selected */}
                        <div
                           style={{
                              ...styles.scrollGradient,
                              background:
                                 "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(0,123,255," +
                                 (isSelected
                                    ? theme === "light"
                                       ? "0.05"
                                       : "0.1"
                                    : "0") +
                                 ") 100%)",
                              opacity: isSelected ? 1 : 0,
                           }}
                        >
                           <i
                              className="bi bi-chevron-down"
                              style={styles.scrollIcon}
                           ></i>
                        </div>
                     </div>
                  </>
               )}
            </div>

            {/* Selected indicator badge */}
            <div
               style={{
                  ...styles.selectedBadge,
                  opacity: isSelected ? 1 : 0,
                  pointerEvents: isSelected ? "auto" : "none",
               }}
            >
               <i className="bi bi-check"></i>
            </div>
         </div>
      </div>
   );
});

// Get styles for CandidateResults component
const getCandidateResultsStyles = (theme) => {
   const isLight = theme === "light";
   return {
      container: {
         marginTop: "1rem",
      },
      loadingContainer: {
         display: "flex",
         justifyContent: "center",
         marginTop: "1rem",
      },
      loadingText: {
         color: "var(--text-primary)",
         marginLeft: "0.5rem",
      },
      errorContainer: {
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.1)"
            : "rgba(239, 68, 68, 0.1)",
         color: "#ef4444",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         marginTop: "1rem",
         border: "1px solid rgba(239, 68, 68, 0.3)",
      },
      warningContainer: {
         backgroundColor: isLight
            ? "rgba(245, 158, 11, 0.1)"
            : "rgba(245, 158, 11, 0.1)",
         color: isLight ? "#f59e0b" : "#fbbf24",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         marginTop: "1rem",
         border: "1px solid rgba(245, 158, 11, 0.3)",
      },
      infoContainer: {
         backgroundColor: isLight
            ? "rgba(66, 133, 244, 0.1)"
            : "rgba(66, 133, 244, 0.1)",
         color: isLight ? "var(--accent-secondary)" : "#63b3ed",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         marginTop: "1rem",
         border: "1px solid rgba(66, 133, 244, 0.3)",
      },
      heading: {
         color: "var(--text-primary)",
         marginBottom: "0.75rem",
         fontFamily: "Inter, sans-serif",
      },
      booksContainer: {
         display: "flex",
         flexWrap: "wrap",
         justifyContent: "center",
         margin: "0 auto",
         maxWidth: "1200px",
         position: "relative",
      },
      bookContainer: {
         display: "flex",
         flexDirection: "column",
         alignItems: "center",
         position: "relative",
         marginBottom: "70px", // Consistent space for the button
      },
      buttonContainer: {
         position: "absolute",
         bottom: "-4rem",
         left: 0,
         right: 0,
         display: "flex",
         justifyContent: "center",
         width: "100%",
         height: "4rem", // Fixed height
      },
      createButton: {
         backgroundColor: "var(--accent-secondary)",
         color: "white",
         borderRadius: "8px",
         padding: "12px 40px",
         fontWeight: "700",
         fontSize: "0.9rem",
         width: "10rem",
         height: "2rem",
         boxShadow: isLight
            ? "0 4px 6px rgba(0,0,0,0.1)"
            : "0 4px 6px rgba(0,0,0,0.2)",
         display: "flex",
         justifyContent: "center",
         alignItems: "center",
         lineHeight: "1",
         marginTop: "1rem",
         border: "none",
         cursor: "pointer",
      },
   };
};

/**
 * CandidateResults Component - Displays generated book options with external Generate button
 */
const CandidateResults = memo(
   ({
      candidateUuids,
      selectedCandidate,
      onSelectCandidate,
      onStartGeneration,
   }) => {
      const { theme } = useTheme();
      const styles = getCandidateResultsStyles(theme);
      const [candidates, setCandidates] = useState([]);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
      const { userId } = useUser();
      const mounted = useRef(true);

      console.log("CandidateResults rendering with uuids:", candidateUuids);

      const fetchCandidateMetadata = async (uuid) => {
         try {
            console.log(`Fetching metadata for candidate ${uuid}`);
            const cdsToken = await getCurrentCDSToken();
            const url = `${config.cdsUrl}/user/audiobooks/${uuid}/metadata?user_id=${userId}`;
            const headers = {
               "Content-Type": "application/json",
               Accept: "application/json",
               Authorization: JSON.stringify(cdsToken),
            };
            const res = await fetch(url, {
               method: "GET",
               headers,
               mode: "cors",
            });
            if (!res.ok) {
               throw new Error(`Error fetching metadata: ${res.status}`);
            }

            const metadata = await res.json();
            console.log(`Metadata received for ${uuid}:`, metadata);
            return metadata;
         } catch (error) {
            console.error("Error fetching metadata for candidate", uuid, error);
            return null;
         }
      };

      useEffect(() => {
         console.log(
            "CandidateResults effect running, candidateUuids:",
            candidateUuids
         );

         mounted.current = true;
         setError(null);

         const loadCandidatesMetadata = async () => {
            if (!candidateUuids || candidateUuids.length === 0) {
               console.log("No candidate UUIDs provided");
               if (mounted.current) {
                  setLoading(false);
               }
               return;
            }

            console.log(
               `Loading metadata for ${candidateUuids.length} candidates`
            );
            setLoading(true);

            try {
               const candidatePromises = candidateUuids.map(async (uuid) => {
                  const metadata = await fetchCandidateMetadata(uuid);
                  return {
                     uuid,
                     title:
                        metadata?.title || `Candidate ${uuid.substring(0, 8)}`,
                     highlight:
                        metadata?.highlight || "No description available",
                     tags: metadata?.tags || [],
                     isPublic: metadata?.isPublic || false,
                  };
               });

               const candidatesWithMetadata = await Promise.all(
                  candidatePromises
               );
               console.log(
                  "All candidates metadata loaded:",
                  candidatesWithMetadata
               );

               if (mounted.current) {
                  setCandidates(candidatesWithMetadata);
                  setLoading(false);
               }
            } catch (error) {
               console.error("Error loading candidates metadata:", error);
               if (mounted.current) {
                  setError("Failed to load book details. Please try again.");
                  setLoading(false);
               }
            }
         };

         loadCandidatesMetadata();

         return () => {
            console.log("CandidateResults cleanup running");
            mounted.current = false;
         };
      }, [candidateUuids, userId]);

      if (loading) {
         return (
            <div style={styles.loadingContainer}>
               <div className="spinner-border text" role="status">
                  <span className="visually-hidden">Loading candidates...</span>
               </div>
               <span style={styles.loadingText}>Loading book options...</span>
            </div>
         );
      }

      if (error) {
         return (
            <div style={styles.errorContainer}>
               <strong>Error:</strong> {error}
            </div>
         );
      }

      if (!candidateUuids || candidateUuids.length === 0) {
         return (
            <div style={styles.warningContainer}>
               No book options were generated. Please try again with a different
               prompt.
            </div>
         );
      }

      if (candidates.length === 0) {
         return (
            <div style={styles.infoContainer}>Loading candidate details...</div>
         );
      }

      return (
         <div style={styles.container}>
            <h3 style={styles.heading}>Select a Book...</h3>
            <div style={styles.booksContainer}>
               {candidates.map((book) => (
                  <div key={book.uuid} style={styles.bookContainer}>
                     <BookCard
                        book={book}
                        isSelected={
                           selectedCandidate &&
                           selectedCandidate.uuid === book.uuid
                        }
                        onSelect={() => onSelectCandidate(book)}
                        theme={theme}
                     />

                     {/* Create button container with absolute positioning */}
                     <div style={styles.buttonContainer}>
                        {/* Only show the button for the selected card */}
                        {selectedCandidate &&
                           selectedCandidate.uuid === book.uuid && (
                              <button
                                 style={styles.createButton}
                                 onClick={() =>
                                    onStartGeneration(selectedCandidate)
                                 }
                              >
                                 Create
                              </button>
                           )}
                     </div>
                  </div>
               ))}
            </div>
         </div>
      );
   }
);

// Get styles for GenerationWsStatus component
function getGenerationWsStyles(theme) {
   const isLight = theme === "light";
   return {
      container: {
         marginTop: "0.75rem",
      },
      loadingContainer: {
         marginTop: "0.75rem",
         display: "flex",
         alignItems: "center",
      },
      loadingText: {
         color: "var(--text-primary)",
      },
      headerContainer: {
         padding: "8px",
         border: `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         borderBottom: (expanded) =>
            expanded
               ? "none"
               : `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         cursor: "pointer",
         display: "flex",
         alignItems: "center",
         justifyContent: "space-between",
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(34, 34, 34, 0.95)",
         position: "relative",
         zIndex: 100,
         color: "var(--text-primary)",
         borderRadius: (expanded) => (expanded ? "4px 4px 0 0" : "4px"),
      },
      headerTitle: {
         fontWeight: "bold",
      },
      headerStatus: {
         display: "flex",
         alignItems: "center",
      },
      connectingText: {
         color: isLight ? "var(--text-muted)" : "#aaa",
         marginRight: "8px",
      },
      errorText: {
         color: "#f87171",
         marginRight: "8px",
      },
      disconnectedText: {
         color: isLight ? "#f59e0b" : "#fbbf24",
         marginRight: "8px",
      },
      completedText: {
         color: isLight ? "var(--accent-primary)" : "#10b981",
         marginRight: "8px",
      },
      contentContainer: {
         border: `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         borderTop: "none",
         padding: "10px",
         marginTop: "0",
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(30, 30, 30, 0.95)",
         color: isLight ? "var(--text-primary)" : "#ddd",
         borderRadius: "0 0 4px 4px",
      },
      statusContainer: {
         marginBottom: "0.5rem",
         border: "1px solid",
         borderRadius: "4px",
      },
      statusDark: {
         backgroundColor: isLight
            ? "rgba(55, 65, 81, 0.05)"
            : "rgba(55, 65, 81, 0.2)",
         borderColor: isLight
            ? "rgba(55, 65, 81, 0.1)"
            : "rgba(55, 65, 81, 0.3)",
         color: isLight ? "var(--text-primary)" : "white",
         padding: "0.75rem",
      },
      statusInfo: {
         backgroundColor: isLight
            ? "rgba(66, 133, 244, 0.05)"
            : "rgba(66, 133, 244, 0.1)",
         borderColor: isLight
            ? "rgba(66, 133, 244, 0.1)"
            : "rgba(66, 133, 244, 0.3)",
         color: isLight ? "var(--accent-secondary)" : "#63b3ed",
         padding: "0.75rem",
      },
      statusPrimary: {
         backgroundColor: isLight
            ? "rgba(79, 70, 229, 0.05)"
            : "rgba(79, 70, 229, 0.1)",
         borderColor: isLight
            ? "rgba(79, 70, 229, 0.1)"
            : "rgba(79, 70, 229, 0.3)",
         color: isLight ? "#4f46e5" : "#818cf8",
         padding: "0.75rem",
      },
      statusSuccess: {
         backgroundColor: isLight
            ? "rgba(16, 185, 129, 0.05)"
            : "rgba(16, 185, 129, 0.1)",
         borderColor: isLight
            ? "rgba(16, 185, 129, 0.1)"
            : "rgba(16, 185, 129, 0.3)",
         color: isLight ? "var(--accent-primary)" : "#10b981",
         padding: "0.75rem",
      },
      statusDanger: {
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         borderColor: isLight
            ? "rgba(239, 68, 68, 0.1)"
            : "rgba(239, 68, 68, 0.3)",
         color: "#ef4444",
         padding: "0.75rem",
      },
      statusWarning: {
         backgroundColor: isLight
            ? "rgba(245, 158, 11, 0.05)"
            : "rgba(245, 158, 11, 0.1)",
         borderColor: isLight
            ? "rgba(245, 158, 11, 0.1)"
            : "rgba(245, 158, 11, 0.3)",
         color: isLight ? "#f59e0b" : "#fbbf24",
         padding: "0.75rem",
      },
      noUpdates: {
         margin: 0,
         color: isLight ? "var(--text-muted)" : "#999",
      },
      updatesList: {
         listStyleType: "disc",
         paddingLeft: "20px",
         margin: 0,
         color: isLight ? "var(--text-primary)" : "#ccc",
      },
   };
}

/**
 * GenerationWsStatus Component - Handles WebSocket for book generation process
 * Modified to only show progress bar and remaining time, without status updates
 */
const GenerationWsStatus = ({
   taskUuid,
   onComplete,
   onError,
   isReadyForWs,
   wsUrlBase,
}) => {
   const { theme } = useTheme();
   const styles = getGenerationWsStyles(theme);
   const [waiting, setWaiting] = useState(true);
   const [updates, setUpdates] = useState([]); // Keep for internal tracking but don't display
   const [expanded, setExpanded] = useState(false); // Keep but won't be used for expansion
   const [connectionStatus, setConnectionStatus] = useState("connecting");
   const [finalStatus, setFinalStatus] = useState(null);
   const [hasLoggedCompletion, setHasLoggedCompletion] = useState(false);
   const [startTime, setStartTime] = useState(new Date());
   const [estimatedTotalMinutes, setEstimatedTotalMinutes] = useState(25); // Default estimate for full book generation
   const [taskProgress, setTaskProgress] = useState(0);
   const [statusMessage, setStatusMessage] = useState(""); // Keep for internal tracking but don't display

   // Refs for WebSocket management
   const wsRef = useRef(null);
   const isClosedRef = useRef(false);
   const intentionalCloseRef = useRef(false);
   const isConnectedRef = useRef(false);
   const reconnectAttemptRef = useRef(0);

   useEffect(() => {
      // Only log if this is a new connection attempt or we haven't logged completion
      if (!hasLoggedCompletion || !isConnectedRef.current) {
         console.log("GenerationWsStatus effect running", {
            taskUuid,
            isReadyForWs,
            isClosed: isClosedRef.current,
            isIntentional: intentionalCloseRef.current,
            isConnected: isConnectedRef.current,
         });
      }

      // Skip connection if conditions aren't met
      if (!isReadyForWs || !taskUuid) {
         // Only log this message once per state change
         if (!hasLoggedCompletion || !isConnectedRef.current) {
            console.log("Not ready for WebSocket or missing taskUuid");

            // If we were previously connected and we're done, mark that we've logged the completion
            if (isConnectedRef.current && intentionalCloseRef.current) {
               setHasLoggedCompletion(true);
            }
         }
         return;
      }

      // Reset hasLoggedCompletion when starting a new connection attempt
      setHasLoggedCompletion(false);

      // Only skip reconnection if we were previously connected and intentionally closed
      if (isConnectedRef.current && intentionalCloseRef.current) {
         console.log(
            "WebSocket was intentionally closed after connecting, keeping UI"
         );
         // Mark that we've logged the completion message
         setHasLoggedCompletion(true);
         return;
      }

      let isSubscribed = true;
      const token = localStorage.getItem("token");

      if (!token) {
         onError("No host token found.");
         return;
      }

      // Clean up any existing connection before creating a new one
      if (wsRef.current) {
         try {
            console.log("Closing existing WebSocket before creating new one");
            wsRef.current.close();
            wsRef.current = null;
         } catch (err) {
            console.error("Error closing existing WebSocket:", err);
         }
      }

      // Reset flags on fresh connection attempt
      isClosedRef.current = false;
      intentionalCloseRef.current = false;

      const url = `${wsUrlBase}?token=${encodeURIComponent(
         token
      )}&task_uuid=${encodeURIComponent(taskUuid)}`;
      console.log("Connecting to WebSocket:", url);

      try {
         const ws = new WebSocket(url);
         wsRef.current = ws;
         setConnectionStatus("connecting");

         ws.onopen = () => {
            console.log("WebSocket connected successfully");
            isConnectedRef.current = true; // Mark as connected
            setConnectionStatus("connected");
            reconnectAttemptRef.current = 0;
         };

         ws.onmessage = (e) => {
            if (!isSubscribed) return;

            try {
               const update = JSON.parse(e.data);
               console.log("WS Update received:", update);

               // Extract progress information if available
               if (update.progress_percentage !== undefined) {
                  setTaskProgress(update.progress_percentage);
               }

               // Extract estimated time information if available
               if (update.estimated_total_minutes !== undefined) {
                  setEstimatedTotalMinutes(update.estimated_total_minutes);
               }

               // Extract detailed status message if available (for internal tracking only)
               if (update.message && update.message !== "updated") {
                  setStatusMessage(update.message);
                  setUpdates((prev) => [...prev, update.message]); // Still track updates internally
               }

               if (update.status === "waiting_in_queue") {
                  console.log("Task is waiting in queue...");
                  setFinalStatus(update);
               } else if (update.status === "done") {
                  console.log("Generation complete, closing WebSocket");

                  // Store the final status for continued display
                  setFinalStatus({
                     status: "completed",
                     message: "Generation successfully completed",
                  });

                  // Only mark as intentionally closed if we were connected
                  if (isConnectedRef.current) {
                     intentionalCloseRef.current = true;
                  }
                  isClosedRef.current = true;
                  setConnectionStatus("completed");
                  setHasLoggedCompletion(true);
                  setTaskProgress(100); // Ensure progress is 100% on completion

                  try {
                     ws.close(1000, "Task completed");
                  } catch (closeErr) {
                     console.error(
                        "Error while closing completed WebSocket:",
                        closeErr
                     );
                  }

                  if (isSubscribed) {
                     onComplete();
                  }
               } else if (
                  update.status === "in_proses" ||
                  update.status === "update"
               ) {
                  // Store this as the latest status in case connection is lost
                  setFinalStatus(update);

                  if (waiting && isSubscribed) {
                     setWaiting(false);
                  }
               } else if (
                  update.status === "failed" ||
                  update.status === "rejected"
               ) {
                  console.error("Generation task failed or rejected:", update);
                  setFinalStatus(update);
                  isClosedRef.current = true;

                  if (isSubscribed) {
                     onError(update.message || "Generation failed");
                  }
               }
            } catch (parseErr) {
               console.error(
                  "Error parsing WebSocket message:",
                  parseErr,
                  "Raw message:",
                  e.data
               );
            }
         };

         ws.onerror = (err) => {
            console.error("WebSocket error occurred:", err);

            if (isSubscribed) {
               setConnectionStatus("error");

               // Only report error to parent if not already closed
               if (!isClosedRef.current) {
                  onError("WebSocket connection error occurred");
               }
            }
         };

         ws.onclose = (event) => {
            console.log(
               `WebSocket closed: Code=${event.code}, Reason=${
                  event.reason || "No reason provided"
               }`
            );
            isClosedRef.current = true;

            // Don't mark as intentionally closed if it was never connected
            if (!isConnectedRef.current) {
               intentionalCloseRef.current = false;
            }

            if (isSubscribed) {
               setConnectionStatus("closed");
            }
         };
      } catch (connectionErr) {
         console.error(
            "Error establishing WebSocket connection:",
            connectionErr
         );
         if (isSubscribed) {
            onError("Failed to establish WebSocket connection");
         }
      }

      // Cleanup function
      return () => {
         console.log("WebSocket cleanup running");
         isSubscribed = false;

         // Only mark as intentionally closed if we were actually connected
         if (isConnectedRef.current) {
            intentionalCloseRef.current = true;
         }

         // Always mark as closed
         isClosedRef.current = true;

         if (wsRef.current) {
            try {
               // Close with normal closure code
               wsRef.current.close(1000, "Component unmounting");
               console.log("WebSocket closed during cleanup");
            } catch (err) {
               console.error("Error closing WebSocket during cleanup:", err);
            }

            wsRef.current = null;
         }
      };
   }, [taskUuid, onComplete, onError, isReadyForWs, wsUrlBase]);

   // UI rendering logic
   if (waiting && !finalStatus) {
      return (
         <div style={styles.loadingContainer}>
            <div className="spinner-border text me-2" role="status">
               <span className="visually-hidden">Loading...</span>
            </div>
            <span style={styles.loadingText}>Waiting in queue...</span>
         </div>
      );
   }


   if (
      (finalStatus && finalStatus.status === "in_proses") ||
      (connectionStatus === "connected" && !isClosedRef.current)
   ) {
      return (
         <div style={styles.container}>

            <AudiobookGenerationProgress
               startTime={startTime}
               estimatedTotalMinutes={estimatedTotalMinutes}
               isComplete={
                  connectionStatus === "completed" ||
                  intentionalCloseRef.current
               }
               taskProgress={taskProgress}
               // Status message param kept but not used in the component
               statusMessage={statusMessage}
            />
         </div>
      );
   }

   // For completed/error states, show a simplified status indicator
   return (
      <div style={styles.container}>

         {finalStatus && finalStatus.status === "failed" && (
            <div style={styles.statusDanger}>
               <div style={styles.flexContainer}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div style={styles.messageContainer}>
                     <strong style={styles.title}>Generation Failed</strong>
                  </div>
               </div>
            </div>
         )}

         {finalStatus && finalStatus.status === "rejected" && (
            <div style={styles.statusWarning}>
               <div style={styles.flexContainer}>
                  <i className="bi bi-x-circle me-2"></i>
                  <div style={styles.messageContainer}>
                     <strong style={styles.title}>Request Rejected</strong>
                  </div>
               </div>
            </div>
         )}

         {(!finalStatus || 
           (finalStatus.status !== "completed" && 
            finalStatus.status !== "failed" && 
            finalStatus.status !== "rejected")) && (
            <div style={styles.statusInfo}>
               <div style={styles.flexContainer}>
                  <div className="spinner-border spinner-border-sm me-2" />
                  <div style={styles.messageContainer}>
                     <strong style={styles.title}>Processing</strong>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

// Get styles for GenerationPanel component
function getGenerationPanelStyles(theme) {
   const isLight = theme === "light";
   return {
      container: {
         padding: "1.5rem",
         border: `1px solid ${isLight ? "var(--border-color)" : "#333"}`,
         borderRadius: "0.5rem",
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(34, 34, 34, 0.98)",
         boxShadow: isLight
            ? "0 4px 8px rgba(0, 0, 0, 0.1)"
            : "0 4px 8px rgba(0, 0, 0, 0.3)",
         color: "var(--text-primary)",
      },
      heading: {
         fontSize: "1.5rem",
         fontWeight: "700",
         marginBottom: "1rem",
         fontFamily: "Inter, sans-serif",
         color: "var(--text-primary)",
      },
      loadingText: {
         color: isLight ? "var(--text-muted)" : "#aaa",
      },
      errorContainer: {
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         color: "#ef4444",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         marginTop: "0.75rem",
         border: "1px solid rgba(239, 68, 68, 0.3)",
      },
      // Updated styles for audio playing completion message
      completionMessage: {
         display: "flex",
         flexDirection: "column",
         alignItems: "center",
         justifyContent: "center",
         padding: "2rem",
         marginTop: "1rem",
         backgroundColor: isLight
            ? "rgba(79, 70, 229, 0.05)"
            : "rgba(79, 70, 229, 0.1)",
         borderRadius: "0.5rem",
         border: `1px solid ${isLight ? "rgba(79, 70, 229, 0.2)" : "rgba(79, 70, 229, 0.3)"}`,
         animation: "pulseGlow 2s infinite",
      },
      completionIcon: {
         fontSize: "3rem",
         color: isLight ? "var(--accent-secondary)" : "#818cf8",
         marginBottom: "1rem",
         animation: "pulse 2s infinite",
      },
      completionText: {
         fontSize: "1.125rem",
         fontWeight: "500",
         textAlign: "center",
         color: "var(--text-primary)",
         fontFamily: "Inter, sans-serif",
      },
      // Add a pulsing animation that will be referenced in global CSS
      "@keyframes pulse": {
         "0%": { transform: "scale(1)" },
         "50%": { transform: "scale(1.1)" },
         "100%": { transform: "scale(1)" }
      },
      "@keyframes pulseGlow": {
         "0%": { boxShadow: `0 0 0 rgba(${isLight ? "79, 70, 229" : "129, 140, 248"}, 0)` },
         "50%": { boxShadow: `0 0 20px rgba(${isLight ? "79, 70, 229" : "129, 140, 248"}, 0.3)` },
         "100%": { boxShadow: `0 0 0 rgba(${isLight ? "79, 70, 229" : "129, 140, 248"}, 0)` }
      }
   };
}

/**
 * GenerationPanel Component - Handles full book generation
 * Modified to remove visible audio player but still initialize global audio
 */
const GenerationPanel = ({ book, adminOptions}) => {
   const { theme } = useTheme();
   const styles = getGenerationPanelStyles(theme);

   // States for generation
   const [generationTask, setGenerationTask] = useState(null);
   const [isReadyForWs, setIsReadyForWs] = useState(false);
   const [showQueueStatus, setShowQueueStatus] = useState(false);
   const [message, setMessage] = useState("");
   const [isLoading, setIsLoading] = useState(true);
   const [doneGenerating, setDoneGenerating] = useState(false);
   const [updatedBook, setUpdatedBook] = useState(null);
   const [estimatedMinutes, setEstimatedMinutes] = useState(null);

   // Get access to the audio context using useContext
   const audioContext = useContext(AudioContext);

   // Ref to track component mount state
   const mountedRef = useRef(true);

   // Add a ref to track API calls for this specific instance
   const apiCallMadeRef = useRef(false);

   // Error handler for generation issues - memoized for stable reference
   const handleGenerationError = useCallback((errorMessage) => {
      if (!mountedRef.current) return;

      console.log(`Generation error: ${errorMessage}`);
      setMessage(errorMessage);
      setGenerationTask(null);
      setIsReadyForWs(false);
      setShowQueueStatus(false);
      setIsLoading(false);
   }, []); // Empty dependency array for stability

   // Updated completion handler that still initializes global audio but doesn't show player
   const handleGenerationComplete = useCallback(async () => {
      if (!mountedRef.current) return;

      console.log("Generation complete, initializing global audio player");
      setDoneGenerating(true);
      setIsLoading(false);
      setIsReadyForWs(false);

      // Use the audio context to load the book when generation is complete
      if (audioContext && audioContext.loadBook && book) {
         try {
            const token = localStorage.getItem("token");
            const response = await fetch("/api/user/library", {
               headers: {
                  Authorization: `Bearer ${token}`,
                  "Cache-Control": "no-cache", // Add no-cache to prevent stale data
               },
            });

            if (!response.ok) {
               console.error(`Couldn't fetch metadata for book: ${book.uuid}`);
            } else {
               const books = await response.json();
               const fetchedBook = books.find((b) => b.uuid === book.uuid);

               if (fetchedBook) {
                  console.log(
                     `Fetched data for ${fetchedBook.uuid}: `,
                     fetchedBook
                  );
                  setUpdatedBook(fetchedBook);

                  // Load the book and attempt autoplay (will handle failures gracefully)
                  audioContext.loadBook(fetchedBook, 1, true);
                  console.log(
                     "Audio initialized for newly generated book:",
                     fetchedBook.title
                  );
               } else {
                  console.error(
                     `Book with UUID ${book.uuid} not found in library`
                  );
               }
            }
         } catch (error) {
            console.error("Error fetching updated book data:", error);
         }
      } else {
         console.warn(
            "Could not initialize audio: audioContext or book unavailable",
            {
               hasContext: !!audioContext,
               hasLoadBook: !!audioContext?.loadBook,
               hasBook: !!book,
            }
         );
      }
   }, [book, audioContext]);

   // When the panel mounts or when "book" changes, request generation
   useEffect(() => {
      if (!book) return;

      console.log(`GenerationPanel effect running for book: ${book.uuid}`);

      // Set mounted ref to true on mount
      mountedRef.current = true;

      const generateBook = async () => {
         // Skip if we've already made the API call in this component instance
         if (apiCallMadeRef.current) {
            console.log(
               `API call already made for book ${book.uuid} in this component instance, skipping`
            );
            return;
         }

         // Mark that we've made the API call for this instance
         apiCallMadeRef.current = true;
         console.log(`Making API call to generate book: ${book.uuid}`);

         const token = localStorage.getItem("token");
         try {

            // Create request body with book UUID
            const requestBody = { uuid: book.uuid };

            // Add admin options if they exist
            if (adminOptions) {
               if (adminOptions.generateAllChapters !== undefined) {
                  requestBody.generateAllChapters = adminOptions.generateAllChapters;
               }
               if (adminOptions.publishImmediately !== undefined) {
                  requestBody.publishImmediately = adminOptions.publishImmediately;
               }
               if (adminOptions.defaultAuthor) {
                  requestBody.defaultAuthor = adminOptions.defaultAuthor;
               }
            }

            const response = await fetch("/api/user/generate/write_book", {
               method: "POST",
               headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + token,
               },
               body: JSON.stringify(requestBody),
            });
            const data = await response.json();

            // Check if component is still mounted before updating state
            if (!mountedRef.current) {
               console.log(
                  "Component unmounted during API call, skipping state updates"
               );
               return;
            }

            if (response.ok || response.status === 202) {
               console.log(`Generation task created: ${data.task_uuid}`);
               setGenerationTask(data.task_uuid);
               setShowQueueStatus(true);

               // Get estimated generation time if provided by the server
               if (data.estimated_minutes) {
                  setEstimatedMinutes(data.estimated_minutes);
                  console.log(
                     `Server provided estimated generation time: ${data.estimated_minutes} minutes`
                  );
               }

               // Add a delay before enabling the WebSocket connection
               console.log(
                  "Waiting 500ms before opening WebSocket connection..."
               );
               setTimeout(() => {
                  if (mountedRef.current) {
                     console.log("Delay complete, ready to connect WebSocket");
                     setIsReadyForWs(true);
                  } else {
                     console.log(
                        "Component unmounted during delay, not setting isReadyForWs"
                     );
                  }
               }, 500); // 500ms delay
            } else {
               console.error(`API error: ${data.error}`);
               handleGenerationError("Error starting generation.");
            }
         } catch (err) {
            // Check if component is still mounted before updating state
            if (!mountedRef.current) return;

            console.error(`Generation error: ${err.message}`);
            handleGenerationError("Generation failed");
         } finally {
            // Check if component is still mounted before updating state
            if (!mountedRef.current) return;

            setIsLoading(false);
         }
      };

      generateBook();

      // Create a single cleanup function
      return () => {
         console.log(`GenerationPanel for book ${book.uuid} is unmounting`);
         // Set mounted ref to false on unmount
         mountedRef.current = false;

         // Set isReadyForWs to false immediately on unmount to prevent unnecessary websocket attempts
         setIsReadyForWs(false);
      };
   }, [book, handleGenerationError, adminOptions]); // handleGenerationError is now stable

   return (
      <div style={styles.container}>
         <h3 style={styles.heading}>Generation Status</h3>
         {isLoading && (
            <div style={styles.loadingText}>Loading generation request...</div>
         )}
         {message && <div style={styles.errorContainer}>{message}</div>}

         {/* WebSocket Status Component - only attempt to connect if not done */}
         {showQueueStatus && generationTask && (
            <GenerationWsStatus
               taskUuid={generationTask}
               onComplete={handleGenerationComplete}
               onError={handleGenerationError}
               isReadyForWs={!doneGenerating && isReadyForWs}
               wsUrlBase={`${config.primaryWsUrl}/api/user/generate/ws/write_book`}
               estimatedMinutes={estimatedMinutes}
            />
         )}

         {/* Show completion message when generation is complete */}
         {doneGenerating && (
            <div style={styles.completionMessage}>
               <div style={styles.completionIcon}>
                  <i className="bi bi-music-note-beamed"></i>
               </div>
               <div style={styles.completionText}>
                  Generation complete! Your audiobook is now playing.
               </div>
            </div>
         )}
      </div>
   );
};

// Get styles for AudiobookPromptSelector component
function getPromptSelectorStyles(theme) {
   const isLight = theme === "light";
   return {
      container: {
         padding: 0,
         border: "none",
      },
      alertContainer: {
         backgroundColor: isLight
            ? "rgba(239, 68, 68, 0.05)"
            : "rgba(239, 68, 68, 0.1)",
         padding: "0.75rem",
         borderRadius: "0.375rem",
         marginTop: "0.75rem",
         border: "1px solid rgba(239, 68, 68, 0.3)",
         color: "#ef4444",
      },
      generationContainer: {
         width: "100%",
         height: "100vh",
         display: "flex",
         justifyContent: "center",
         alignItems: "center",
         padding: "2rem",
      },
      generationPanel: {
         backgroundColor: isLight
            ? "var(--bg-secondary)"
            : "rgba(20, 20, 20, 0.9)",
         borderRadius: "0.75rem",
         padding: "2rem",
         maxWidth: "800px",
         width: "100%",
         textAlign: "center",
         boxShadow: isLight
            ? "0 4px 12px rgba(0, 0, 0, 0.1)"
            : "0 4px 12px rgba(0, 0, 0, 0.3)",
      },
   };
}

/**
 * Main AudiobookPromptSelector Component
 *
 * This updated version supports the full generation workflow with progress indicator:
 * 1. Show prompt input and handle candidate generation
 * 2. Let user select a candidate
 * 3. Handle generation of the selected book with visual progress indicator
 * 4. Displays completion message when done (instead of audio player)
 */
const AudiobookPromptSelector = ({ onBookSelected }) => {
   const { theme } = useTheme(); // Get current theme
   const styles = getPromptSelectorStyles(theme);

   // State for both selection and generation phases
   const [message, setMessage] = useState("");
   const [generationTask, setGenerationTask] = useState(null);
   const [pendingCandidateUuids, setPendingCandidateUuids] = useState(null);
   const [candidateUuids, setCandidateUuids] = useState(null);
   const [selectedCandidate, setSelectedCandidate] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [isReadyForWs, setIsReadyForWs] = useState(false);
   const [showQueueStatus, setShowQueueStatus] = useState(false);
   const [estimatedMinutes, setEstimatedMinutes] = useState(15); // Default value

   const [phase, setPhase] = useState("selection"); // "selection" or "generation"
   const [bookToGenerate, setBookToGenerate] = useState(null);

   const generatedBookIds = useRef(new Set());

   // Add mountedRef to track component mount state
   const mountedRef = useRef(true);

   const [adminOptions, setAdminOptions] = useState(null);

   // Use console logging to track state changes
   useEffect(() => {
      console.log("AudiobookPromptSelector state update:", {
         phase,
         bookToGenerate: bookToGenerate?.title,
         generationTask,
         pendingCandidateUuids: pendingCandidateUuids
            ? `${pendingCandidateUuids.length} items`
            : null,
         candidateUuids: candidateUuids
            ? `${candidateUuids.length} items`
            : null,
         isLoading,
         isReadyForWs,
         showQueueStatus,
      });
   }, [
      phase,
      bookToGenerate,
      generationTask,
      pendingCandidateUuids,
      candidateUuids,
      isLoading,
      isReadyForWs,
      showQueueStatus,
   ]);

   // Setup cleanup on unmount
   useEffect(() => {
      console.log("AudiobookPromptSelector component mounted");
      mountedRef.current = true;

      return () => {
         console.log("AudiobookPromptSelector component unmounting");
         mountedRef.current = false;
      };
   }, []);

   // Handle starting the full book generation and trigger the callback
   const handleStartGeneration = (book) => {
      console.log(`Selected book for generation: ${book.uuid}`);

      // Check if we've already started generation for this book
      if (generatedBookIds.current.has(book.uuid)) {
         console.log(
            `Generation already initiated for book ${book.uuid}, ignoring duplicate request`
         );
         return;
      }

      // Mark this book as being generated
      generatedBookIds.current.add(book.uuid);
      console.log(`Added book ${book.uuid} to generated books set`);

      // Set the book to generate and change the phase
      setBookToGenerate(book);
      setPhase("generation");

      // Call the external callback if provided (for backward compatibility)
      if (onBookSelected) {
         onBookSelected(book);
      }
   };

   // Handle the initial prompt submission for candidate generation
   const handleGenerate = async (prompt, adminOptions = null) => {
      console.log("Starting generation with prompt:", prompt);
      
      if (adminOptions) {
         console.log("Admin options provided:", adminOptions);
         setAdminOptions(adminOptions);
      }

      // Reset state
      setMessage("");
      setPendingCandidateUuids(null);
      setCandidateUuids(null);
      setGenerationTask(null);
      setSelectedCandidate(null);
      setIsLoading(true);
      setIsReadyForWs(false);
      setShowQueueStatus(false);

      try {
         const token = localStorage.getItem("token");
         if (!token) {
            setMessage("Error: No authentication token found. Please log in.");
            setIsLoading(false);
            return;
         }

         console.log("Sending generation request to API");
         
         // Create request body - include admin options if provided
         const requestBody = {
            prompt
         };

         // Add admin options if they exist
         if (adminOptions) {
            requestBody.generateAllChapters = adminOptions.generateAllChapters;
            requestBody.publishImmediately = adminOptions.publishImmediately;

            if (adminOptions.defaultAuthor) {
               requestBody.defaultAuthor = adminOptions.defaultAuthor;
            }
         }

         console.log(JSON.stringify(requestBody));

         const response = await fetch("/api/user/generate/user_spce", {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
               Authorization: "Bearer " + token,
            },
            body: JSON.stringify(requestBody),
         });

         const data = await response.json();
         console.log("Generation API response:", data);

         if (!mountedRef.current) return;

         if (response.ok || response.status === 202) {
            const { task_uuid, uuids, estimated_minutes } = data;

            console.log(
               `Got task_uuid: ${task_uuid} and ${
                  uuids ? uuids.length : 0
               } candidate UUIDs`
            );

            // Store the task UUID to monitor progress
            setGenerationTask(task_uuid);

            // Store the candidate UUIDs for later, but don't set them in state until generation is complete
            if (uuids && uuids.length > 0) {
               setPendingCandidateUuids(uuids);
            }

            // If the server provided an estimated time, update our state
            if (estimated_minutes) {
               setEstimatedMinutes(estimated_minutes);
               console.log(
                  `Server estimated generation time: ${estimated_minutes} minutes`
               );
            }

            // First set the show queue status
            setShowQueueStatus(true);

            // Add a delay before setting isReadyForWs to true
            console.log("Waiting 500ms before opening WebSocket connection...");
            setTimeout(() => {
               if (mountedRef.current) {
                  console.log("Delay complete, ready to connect WebSocket");
                  setIsReadyForWs(true);
               }
            }, 500); // 500ms delay
         } else {
            console.error("API error:", data);
            setMessage(
               "Error: " + (data.error || "Generation request failed.")
            );
            setIsLoading(false);
         }
      } catch (error) {
         console.error("Generation request error:", error);
         setMessage("Error: Unable to send generation request.");
         setIsLoading(false);
      }
   };

   // Enhanced handleGenerationComplete for the selection phase
   const handleGenerationComplete = useCallback(
      (wsUuids) => {
         if (!mountedRef.current) return;

         console.log("Generation complete callback triggered");

         // Update UI state
         setMessage("");
         setIsReadyForWs(false);
         setShowQueueStatus(false);
         setIsLoading(false);

         // NOW set the candidateUuids to trigger the CandidateResults component
         // Use either the UUIDs passed from WS or the pending ones stored from API
         setCandidateUuids(pendingCandidateUuids || []);

         console.log(
            "Setting candidate UUIDs after completion:",
            pendingCandidateUuids
         );
      },
      [pendingCandidateUuids]
   );

   // Error handler for generation issues
   const handleGenerationError = useCallback(
      (errorMessage) => {
         if (!mountedRef.current) return;

         console.error("Generation error:", errorMessage);

         setMessage("Error: " + errorMessage);
         setGenerationTask(null);
         setIsReadyForWs(false);
         setShowQueueStatus(false);
         setIsLoading(false);

         // Even on error, if we have candidate UUIDs, we can still display them
         if (pendingCandidateUuids && pendingCandidateUuids.length > 0) {
            setCandidateUuids(pendingCandidateUuids);
         }
      },
      [pendingCandidateUuids]
   );

   // Render the component based on the current phase
   return (
      <div style={styles.container}>
         {phase === "selection" && (
            <>
               <div className="mb-3">
                  <PromptInput
                     onSubmit={handleGenerate}
                     isLoading={isLoading}
                  />
               </div>

               {/* Show message only for errors, not for normal status updates */}
               {message && message.includes("Error") && (
                  <div style={styles.alertContainer}>{message}</div>
               )}

               {/* Only show queue status if we have a task and are ready for WebSocket */}
               {showQueueStatus && generationTask && isReadyForWs && (
                  <QueueStatus
                     taskUuid={generationTask}
                     onComplete={handleGenerationComplete}
                     onError={handleGenerationError}
                     isReadyForWs={isReadyForWs}
                     wsUrlBase={`${config.primaryWsUrl}/api/user/generate/ws/user_spce`}
                     candidateUuids={pendingCandidateUuids}
                  />
               )}

               {/* Show candidates only after generation is complete and we have set candidateUuids */}
               {candidateUuids && candidateUuids.length > 0 && (
                  <CandidateResults
                     candidateUuids={candidateUuids}
                     selectedCandidate={selectedCandidate}
                     onSelectCandidate={setSelectedCandidate}
                     onStartGeneration={handleStartGeneration}
                  />
               )}
            </>
         )}

         {phase === "generation" && bookToGenerate && (
            <div style={styles.generationContainer}>
               <div style={styles.generationPanel}>
                  <GenerationPanel
                     book={bookToGenerate}
                     adminOptions={adminOptions}
                  />
               </div>
            </div>
         )}
      </div>
   );
};

export default AudiobookPromptSelector;
